import { useRef } from 'react';

import type { ContentExtensionMetrics } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';
import { retrieveContentExtensionMetrics } from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

type ContentBody = Parameters<typeof retrieveContentExtensionMetrics>[0] | null | undefined;
type CustomPerfData = { contentExtensionMetrics?: ContentExtensionMetrics } | null | undefined;

export function useContentExtensionMetrics(
	contentBody: ContentBody,
	customPerfData: CustomPerfData,
	contentId?: string,
): void {
	const memoRef = useRef({
		contentBody: undefined as typeof contentBody,
		contentId: undefined as typeof contentId,
		contentExtensionMetrics: undefined as undefined | ContentExtensionMetrics | {},
	});

	memoRef.current.contentId = contentId;

	if (
		customPerfData &&
		(memoRef.current.contentBody !== contentBody || !('contentExtensionMetrics' in customPerfData))
	) {
		// Keep the fallback lazy so the renderer can supply metrics from its existing traversal.
		// If needed, the fallback can share the page's parsed ADF behind the parsing experiment.
		let propertyValue: undefined | ContentExtensionMetrics;

		Object.defineProperty(customPerfData, 'contentExtensionMetrics', {
			enumerable: true,
			get() {
				if (propertyValue) {
					return propertyValue;
				}
				memoRef.current.contentBody = contentBody;
				const contentExtensionMetrics = contentBody
					? retrieveContentExtensionMetrics(contentBody, memoRef.current.contentId)
					: {};
				memoRef.current.contentExtensionMetrics = contentExtensionMetrics;
				return memoRef.current.contentExtensionMetrics;
			},
			set(value: typeof propertyValue) {
				propertyValue = value;
			},
		});
	}
}
